import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaPaperPlane,
    FaArrowLeft,
    FaImage,
    FaSmile,
    FaEllipsisV,
    FaCheck,
    FaCheckDouble
} from "react-icons/fa";
import {
    HiOutlineMail,
    HiOutlineAcademicCap,
    HiOutlineLocationMarker
} from "react-icons/hi";
import axios from "axios";
import { io } from "socket.io-client";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import EmojiPicker from 'emoji-picker-react';

const Chat = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const user = location.state?.user;
    const token = Cookies.get("userToken");
    console.log(user);

    const loggedInUser = JSON.parse(localStorage.getItem("userData"));

    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [typing, setTyping] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [socket, setSocket] = useState(null);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const chatContainerRef = useRef(null);
    const emojiPickerRef = useRef(null);

    /* ================= SOCKET CONNECTION ================= */
    useEffect(() => {
        const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
            withCredentials: true,
            transports: ['websocket', 'polling']
        });
        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (socket && loggedInUser?._id) {
            socket.emit("join", loggedInUser._id);
        }
    }, [socket, loggedInUser]);

    /* ================= FETCH OLD MESSAGES ================= */
    useEffect(() => {
        const fetchMessages = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/apis/user/messages/${id}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        withCredentials: true,
                    }
                );

                if (res.data && Array.isArray(res.data)) {
                    setMessages(res.data);

                    // Mark messages as seen when fetched
                    if (res.data.length > 0) {
                        markMessagesAsSeen();
                    }
                }
            } catch (error) {
                console.log(error);
            }
        };

        if (id) fetchMessages();
    }, [id, token]);

    /* ================= MARK MESSAGES AS SEEN ================= */
    const markMessagesAsSeen = async () => {
        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/apis/user/seen`,
                { senderId: id },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            // Emit socket event for real-time seen status
            if (socket) {
                // messageSenderId: the person who sent the messages (the other user)
                // messageViewerId: the person who viewed them (current user)
                socket.emit("messagesSeen", {
                    messageSenderId: id,
                    messageViewerId: loggedInUser?._id
                });
            }

            // Update local messages
            setMessages(prev => prev.map(msg => {
                const senderId = typeof msg.sender === "object" ? msg.sender._id : msg.sender;
                if (senderId?.toString() === id?.toString()) {
                    return { ...msg, seen: true };
                }
                return msg;
            }));
        } catch (error) {
            console.error("Error marking messages as seen:", error);
        }
    };

    /* ================= RECEIVE REAL-TIME MESSAGE ================= */
    useEffect(() => {
        if (!socket) return;

        const handleReceiveMessage = (newMessage) => {
            // Check if message is for this chat
            const senderId = typeof newMessage.sender === "object"
                ? newMessage.sender._id
                : newMessage.sender;

            const receiverId = typeof newMessage.receiver === "object"
                ? newMessage.receiver._id
                : newMessage.receiver;

            if (senderId === id || receiverId === id) {
                setMessages((prev) => {
                    const exists = prev.some((msg) => msg._id === newMessage._id);
                    if (exists) return prev;

                    // If message is from the other user, mark it as seen immediately
                    if (senderId === id) {
                        // Call mark as seen for this message
                        markMessagesAsSeen();
                        return [...prev, { ...newMessage, seen: true }];
                    }

                    return [...prev, newMessage];
                });
            }
        };

        const handleMessagesSeen = ({ messageViewerId }) => {
            // When the other user marks messages as seen
            // messageViewerId is the person who viewed the messages (should be the other user)
            if (messageViewerId === id) {
                setMessages((prev) =>
                    prev.map(msg => {
                        const msgSenderId = typeof msg.sender === "object"
                            ? msg.sender._id
                            : msg.sender;

                        // Only update messages sent by current user
                        if (msgSenderId?.toString() === loggedInUser?._id?.toString()) {
                            return { ...msg, seen: true };
                        }
                        return msg;
                    })
                );
            }
        };

        const handleMessageSeen = ({ messageId, messageViewerId }) => {
            // When a specific message is marked as seen
            if (messageViewerId === id) {
                setMessages((prev) =>
                    prev.map(msg => {
                        const msgSenderId = typeof msg.sender === "object"
                            ? msg.sender._id
                            : msg.sender;

                        // Only update messages sent by current user
                        if (msgSenderId?.toString() === loggedInUser?._id?.toString() &&
                            msg._id === messageId) {
                            return { ...msg, seen: true };
                        }
                        return msg;
                    })
                );
            }
        };

        const handleTyping = ({ userId, typing }) => {
            if (userId === id) {
                setIsTyping(typing);
            }
        };

        socket.on("receiveMessage", handleReceiveMessage);
        socket.on("messagesSeen", handleMessagesSeen);
        socket.on("messageSeen", handleMessageSeen);
        socket.on("typing", handleTyping);

        return () => {
            socket.off("receiveMessage", handleReceiveMessage);
            socket.off("messagesSeen", handleMessagesSeen);
            socket.off("messageSeen", handleMessageSeen);
            socket.off("typing", handleTyping);
        };
    }, [socket, id, loggedInUser]);

    /* ================= AUTO SCROLL ================= */
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    /* ================= TYPING INDICATOR SENDER ================= */
    useEffect(() => {
        if (!socket) return;

        if (message) {
            if (!typing) {
                setTyping(true);
                socket.emit("typing", { receiverId: id, typing: true });
            }

            const timeout = setTimeout(() => {
                setTyping(false);
                socket.emit("typing", { receiverId: id, typing: false });
            }, 2000);

            return () => clearTimeout(timeout);
        } else {
            setTyping(false);
            socket.emit("typing", { receiverId: id, typing: false });
        }
    }, [message, id, socket]);

    /* ================= CLICK OUTSIDE EMOJI PICKER ================= */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    /* ================= SEND TEXT MESSAGE ================= */
    const handleSendMessage = async () => {
        if (!message.trim()) return;

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/apis/user/sendMess`,
                {
                    receiverId: id,
                    message,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    withCredentials: true,
                }
            );

            // Add message to local state
            setMessages((prev) => [...prev, { ...res.data, seen: false }]);
            setMessage("");
            setShowEmojiPicker(false);
        } catch (error) {
            console.log(error);
            toast.error("Failed to send message");
        }
    };

    /* ================= SEND IMAGE MESSAGE ================= */
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("messageImage", file);
        formData.append("receiverId", id);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/apis/user/sendMess`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                    withCredentials: true,
                }
            );

            setMessages((prev) => [...prev, { ...res.data, seen: false }]);
            toast.success("Image sent successfully");
        } catch (error) {
            console.log(error);
            toast.error("Failed to send image");
        }
    };

    /* ================= EMOJI PICKER ================= */
    const onEmojiClick = (emojiObject) => {
        setMessage(prev => prev + emojiObject.emoji);
        setShowEmojiPicker(false);
    };

    /* ================= FORMAT TIME ================= */
    const formatMessageTime = (date) => {
        const now = new Date();
        const messageDate = new Date(date);

        if (messageDate.toDateString() === now.toDateString()) {
            return messageDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        } else if (messageDate.getDate() === now.getDate() - 1) {
            return "Yesterday";
        } else {
            return messageDate.toLocaleDateString([], { month: "short", day: "numeric" });
        }
    };

    /* ================= CHECK IF MESSAGE IS FROM CURRENT USER ================= */
    const isMessageFromMe = (msg) => {
        const senderId = typeof msg.sender === "object" ? msg.sender._id : msg.sender;
        return senderId?.toString() === loggedInUser?._id?.toString();
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const messageVariants = {
        hidden: { opacity: 0, y: 20, scale: 0.95 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                delay: i * 0.05,
                duration: 0.3,
                ease: "easeOut"
            }
        })
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-3 sm:p-4 md:p-6 lg:p-8"
        >
            {/* Animated Background Elements - Light Gray */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
                    animate={{
                        x: [0, 50, 0],
                        y: [0, -30, 0],
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />
                <motion.div
                    className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
                    animate={{
                        x: [0, -50, 0],
                        y: [0, 30, 0],
                    }}
                    transition={{
                        duration: 18,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />
            </div>

            <div className="max-w-7xl mx-auto bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-gray-200 min-h-[85vh]">
                <div className="flex flex-col md:flex-row h-full">

                    {/* LEFT USER CARD - Light Gray */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="hidden md:flex md:w-1/3 bg-gradient-to-br from-gray-50 via-white to-gray-50 p-6 lg:p-8 flex-col items-center border-r border-gray-200"
                    >
                        <div className="relative group">
                            <img
                                src={user?.profileImage || user?.profile?.userProfileImage || "/assets/DefaultImage.jpg"}
                                alt={user?.fullName}
                                className="w-32 h-32 lg:w-36 lg:h-36 rounded-full border-4 border-gray-200 shadow-xl object-cover group-hover:border-gray-300 transition-all duration-300"
                            />
                            <div className="absolute bottom-2 right-2 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>

                        <h2 className="mt-6 text-xl lg:text-2xl font-bold text-gray-700 text-center">
                            {user?.fullName || "User"}
                        </h2>

                        <p className="text-sm text-gray-500 mt-2 text-center flex items-center justify-center gap-2">
                            <HiOutlineMail className="w-4 h-4 text-gray-400" />
                            {user?.email}
                        </p>

                        <div className="w-full mt-8 space-y-4">
                            <div className="flex items-center gap-3 p-3 bg-white/80 rounded-xl border border-gray-200">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <HiOutlineAcademicCap className="w-4 h-4 text-gray-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Department</p>
                                    <p className="text-sm font-medium text-gray-800">{user?.department || "Not specified"}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-3 bg-white/80 rounded-xl border border-gray-200">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <HiOutlineAcademicCap className="w-4 h-4 text-gray-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">College</p>
                                    <p className="text-sm font-medium text-gray-800">
                                        {user?.userCollegeName || user?.collegeName || user?.academicInformation?.collegeName || "Not specified"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* RIGHT CHAT SECTION */}
                    <div className="flex flex-col w-full md:w-2/3 h-[85vh]">

                        {/* MOBILE HEADER - Light Gray */}
                        <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
                            <div className="flex items-center gap-3">
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => navigate(-1)}
                                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition"
                                >
                                    <FaArrowLeft size={18} />
                                </motion.button>
                                <div className="flex items-center gap-3">
                                    <img
                                        src={user?.profileImage || user?.image || "/assets/DefaultImage.jpg"}
                                        alt={user?.fullName}
                                        className="w-10 h-10 rounded-full border-2 border-gray-300 object-cover"
                                    />
                                    <div>
                                        <h2 className="font-semibold text-gray-700">
                                            {user?.fullName || "User"}
                                        </h2>
                                        {isTyping && (
                                            <p className="text-xs text-gray-500">typing...</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition">
                                <FaEllipsisV size={16} />
                            </button>
                        </div>

                        {/* DESKTOP HEADER - Light Gray */}
                        <div className="hidden md:flex items-center justify-between p-4 border-b border-gray-200 bg-white/50 backdrop-blur-sm">
                            <div className="flex items-center gap-3">
                                <img
                                    src={user?.profileImage || user?.image || "/assets/DefaultImage.jpg"}
                                    alt={user?.fullName}
                                    className="w-10 h-10 rounded-full border-2 border-gray-300 object-cover"
                                />
                                <div>
                                    <h2 className="font-semibold text-gray-700">
                                        {user?.fullName || "User"}
                                    </h2>
                                    {isTyping && (
                                        <motion.p
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="text-xs text-gray-500"
                                        >
                                            typing...
                                        </motion.p>
                                    )}
                                </div>
                            </div>
                            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition">
                                <FaEllipsisV size={16} />
                            </button>
                        </div>

                        {/* MESSAGES - Light Gray */}
                        <div
                            ref={chatContainerRef}
                            className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 bg-gradient-to-b from-gray-50/30 to-white scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent"
                        >
                            <AnimatePresence>
                                {messages.map((msg, index) => {
                                    const isMe = isMessageFromMe(msg);

                                    return (
                                        <motion.div
                                            key={msg._id}
                                            custom={index}
                                            variants={messageVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit={{ opacity: 0, x: isMe ? 20 : -20 }}
                                            className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                                        >
                                            <div className="flex items-end gap-2 max-w-[85%] md:max-w-[70%]">
                                                {!isMe && (
                                                    <img
                                                        src={user?.profileImage || user?.image || "/assets/DefaultImage.jpg"}
                                                        alt={user?.fullName}
                                                        className="w-8 h-8 rounded-full border-2 border-gray-200 object-cover hidden sm:block"
                                                    />
                                                )}

                                                <div
                                                    className={`relative group px-4 py-2.5 rounded-2xl shadow-md ${isMe
                                                            ? "bg-gray-700 text-white rounded-br-none"
                                                            : "bg-white text-gray-700 rounded-bl-none border border-gray-200"
                                                        }`}
                                                >
                                                    {msg.messageType === "image" ? (
                                                        <img
                                                            src={msg.image}
                                                            alt="chat"
                                                            className="rounded-lg max-h-60 w-auto object-cover cursor-pointer hover:opacity-90 transition"
                                                            onClick={() => window.open(msg.image, '_blank')}
                                                        />
                                                    ) : (
                                                        <p className="text-sm md:text-base break-words">{msg.message}</p>
                                                    )}

                                                    <div className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${isMe ? 'text-gray-300' : 'text-gray-400'}`}>
                                                        <span>{formatMessageTime(msg.createdAt)}</span>
                                                        {isMe && (
                                                            <span className="ml-1">
                                                                {msg.seen ? (
                                                                    <FaCheckDouble className="w-3 h-3 text-blue-400" />
                                                                ) : (
                                                                    <FaCheck className="w-3 h-3" />
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Message tail */}
                                                    <div className={`absolute bottom-0 ${isMe ? 'right-0' : 'left-0'} transform translate-y-1/2`}>
                                                        <div className={`w-4 h-4 ${isMe ? 'bg-gray-700' : 'bg-white'} transform rotate-45 shadow-md`}></div>
                                                    </div>
                                                </div>

                                                {isMe && (
                                                    <img
                                                        src={loggedInUser?.image || "/assets/DefaultImage.jpg"}
                                                        alt={loggedInUser?.fullName}
                                                        className="w-8 h-8 rounded-full border-2 border-gray-200 object-cover hidden sm:block"
                                                    />
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>

                            {/* Typing indicator - Light Gray */}
                            {isTyping && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex justify-start"
                                >
                                    <div className="flex items-center gap-2 bg-white rounded-2xl px-4 py-3 shadow-md border border-gray-200">
                                        <img
                                            src={user?.profileImage || user?.image || "/assets/DefaultImage.jpg"}
                                            alt={user?.fullName}
                                            className="w-6 h-6 rounded-full border-2 border-gray-200 object-cover"
                                        />
                                        <div className="flex gap-1">
                                            <motion.span
                                                animate={{ y: [0, -5, 0] }}
                                                transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                                                className="w-2 h-2 bg-gray-400 rounded-full"
                                            />
                                            <motion.span
                                                animate={{ y: [0, -5, 0] }}
                                                transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                                                className="w-2 h-2 bg-gray-400 rounded-full"
                                            />
                                            <motion.span
                                                animate={{ y: [0, -5, 0] }}
                                                transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                                                className="w-2 h-2 bg-gray-400 rounded-full"
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {/* INPUT AREA - Light Gray */}
                        <div className="relative p-3 md:p-4 bg-white border-t border-gray-200">
                            <div className="flex items-center gap-2 md:gap-3">
                                {/* IMAGE BUTTON */}
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => fileInputRef.current.click()}
                                    className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition"
                                >
                                    <FaImage size={18} />
                                </motion.button>

                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    onChange={handleImageUpload}
                                    hidden
                                />

                                {/* EMOJI BUTTON */}
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                    className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition relative"
                                >
                                    <FaSmile size={18} />
                                </motion.button>

                                {/* EMOJI PICKER */}
                                <AnimatePresence>
                                    {showEmojiPicker && (
                                        <motion.div
                                            ref={emojiPickerRef}
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            className="absolute bottom-20 left-0 z-50"
                                        >
                                            <EmojiPicker
                                                onEmojiClick={onEmojiClick}
                                                width={300}
                                                height={400}
                                                theme="light"
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* TEXT INPUT */}
                                <input
                                    type="text"
                                    placeholder="Type a message..."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={(e) =>
                                        e.key === "Enter" && handleSendMessage()
                                    }
                                    className="flex-1 px-4 py-2.5 rounded-full border border-gray-200 focus:ring-2 focus:ring-gray-300 focus:outline-none transition bg-gray-50/30 focus:bg-white text-gray-700 placeholder-gray-400"
                                />

                                {/* SEND BUTTON */}
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handleSendMessage}
                                    disabled={!message.trim()}
                                    className={`p-3 rounded-full shadow-md transition ${message.trim()
                                            ? "bg-gray-700 text-white hover:bg-gray-800"
                                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                        }`}
                                >
                                    <FaPaperPlane size={14} />
                                </motion.button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default Chat;